import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'

import { BulkUserActions } from './BulkUserActions'
import { UserTableModals } from './UserTableModals'
import { buildUserTableColumns, buildRows } from './userTableColumns'
import { buildActionsForUser } from './userTableActions'

export const UserTable = ({
  playbooks,
  editUsers,
  isAdmin,
  loading,
  organizationId,
  qaEnabled,
  qaCopilotEnabled,
  agentGPTEnabled,
  contentCustomizationEnabled,
  tags,
  tagCategories,
  users,
  handleUserUpdate,
  generateTemporaryLink,
  generatePassphrase,
  orgRealtimeCoachingAccess,
  orgDesktopEnterpriseEnabled,
  fetchUsers,
  userFilter,
  restoreUser,
  flags,
  toolsEnabled,
}) => {
  const currentUser = useSelector((state) => state.currentUser)
  const [activeModal, setActiveModal] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])

  useEffect(() => {
    setSelectedRows([])
  }, [userFilter])

  const openRowModal = (modalType, row) => {
    setActiveModal(modalType)
    setActiveRow(row)
  }

  const closeActiveModal = () => {
    setActiveModal(null)
    setActiveRow(null)
  }

  const handleBulkSelectSingle = (rowId) => {
    setSelectedRows((previousSelectedRows) => {
      if (previousSelectedRows.includes(rowId)) {
        return previousSelectedRows.filter((id) => id !== rowId)
      }

      return [...previousSelectedRows, rowId]
    })
  }

  const handleSelectAll = (rowsToSelect) => {
    setSelectedRows(rowsToSelect.map((row) => row.id))
  }

  const handleDeselectAll = () => {
    setSelectedRows([])
  }

  const columns = useMemo(
    () =>
      buildUserTableColumns({
        playbooks,
        tags,
        tagCategories,
        flags,
        contentCustomizationEnabled,
        openRowModal,
      }),
    [playbooks, tags, tagCategories, flags, contentCustomizationEnabled]
  )

  const rows = useMemo(() => {
    return buildRows(users, { playbooks, tagCategories, agentGPTEnabled })
  }, [users, tagCategories, playbooks, agentGPTEnabled])

  const actions = buildActionsForUser({
    isAdmin,
    editUsers,
    actionContext: {
      isAdmin,
      editUsers,
      qaEnabled,
      qaCopilotEnabled,
      userFilter,
      currentUserEmail: currentUser.username,
      openRowModal,
      restoreUser,
    },
  })

  return (
    <>
      {rows && (
        <Card className="no-padding user-table-sticky-select" data-testid="user-table">
          <AdvancedTable
            loading={loading}
            rows={rows}
            columns={columns}
            actions={actions}
            defaultOrderBy="last_name"
            pagination
            stickyAction
            bulkSelectable={editUsers || isAdmin}
            selectedRows={selectedRows}
            handleBulkSelectSingle={handleBulkSelectSingle}
            handleSelectAll={handleSelectAll}
            handleDeselectAll={handleDeselectAll}
            bulkSelectAction={
              <BulkUserActions
                selectedUserIds={selectedRows}
                selectedUsers={rows.filter((row) => selectedRows.includes(row.id))}
                organizationId={organizationId}
                playbooks={playbooks}
                tags={tags}
                tagCategories={tagCategories}
                handleDeselectAll={handleDeselectAll}
                fetchUsers={fetchUsers}
                userFilter={userFilter}
              />
            }
            itemName="user"
          />
        </Card>
      )}

      <UserTableModals
        activeModal={activeModal}
        activeRow={activeRow}
        closeActiveModal={closeActiveModal}
        handleUserUpdate={handleUserUpdate}
        generateTemporaryLink={generateTemporaryLink}
        generatePassphrase={generatePassphrase}
        playbooks={playbooks}
        tags={tags}
        tagCategories={tagCategories}
        qaEnabled={qaEnabled}
        qaCopilotEnabled={qaCopilotEnabled}
        agentGPTEnabled={agentGPTEnabled}
        orgRealtimeCoachingAccess={orgRealtimeCoachingAccess}
        orgDesktopEnterpriseEnabled={orgDesktopEnterpriseEnabled}
        flags={flags}
        toolsEnabled={toolsEnabled}
      />
    </>
  )
}
